/**
 * Types for the Statistics API
 */

import type { ProxyExtensionMeta } from "@/services/api/widget/types";

export type { ProxyExtensionMeta };

export type ResourceType = "product" | "portfolio" | "benchmark" | "index";

export interface StatisticsResource {
  resource_type: ResourceType;
  resource_id: string;
  stream_type?: string;
  benchmark_id?: string;
}

// Available statistics fields matching the backend enum
export type StatisticField =
  // Performance stats
  | "cumulative_performance"
  | "annualized_performance"
  // Risk stats
  | "volatility"
  | "upside_volatility"
  | "downside_volatility"
  // Drawdown stats
  | "maximum_drawdown"
  | "peak_date"
  | "trough_date"
  | "recovery_date"
  | "peak_to_trough_days"
  | "time_to_recovery"
  | "ulcer_index"
  | "pain_index"
  | "average_drawdown_duration"
  // Ratios
  | "sharpe_ratio"
  | "sortino_ratio"
  | "calmar_ratio"
  | "return_to_drawdown_ratio"
  | "omega_ratio"
  | "gain_loss_ratio"
  | "profit_factor"
  // Other stats
  | "num_positive_periods"
  | "num_negative_periods"
  | "percent_positive_periods"
  | "percent_negative_periods"
  | "best_period"
  | "worst_period"
  | "average_positive_periods"
  | "average_negative_periods"
  | "longest_winning_streak"
  | "longest_losing_streak"
  | "skewness"
  | "kurtosis"
  | "historical_var"
  | "historical_cvar"
  // Relative statistics
  | "relative_cumulative_performance"
  | "relative_annualized_performance"
  | "relative_volatility"
  | "beta"
  | "correlation"
  | "tracking_error"
  | "information_ratio";

export interface StatisticsRequest {
  resources: StatisticsResource[];
  fields: StatisticField[];
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  frequency?: "daily" | "weekly" | "monthly";
  time_range_preset?: string; // Optional: MAX, MAX_COMMON, 1Y, 3Y, etc. - overrides start_date
}

export interface StatisticValue {
  field: StatisticField;
  value: number | string | null;
  error?: string;
  metadata?: Record<string, any>;
}

export interface ResourceStatistics {
  resource_type: ResourceType;
  resource_id: string;
  stream_type?: string;
  statistics: StatisticValue[];
  metadata?: Record<string, any>;
}

export interface StatisticsResponse {
  start_date: string;
  end_date: string;
  frequency: string;
  resources: ResourceStatistics[];
  metadata?: Record<string, any>;
}

// Friendly labels for statistics fields
export const STATISTIC_FIELD_LABELS: Record<StatisticField, string> = {
  cumulative_performance: "Cumulative Performance",
  annualized_performance: "Annualized Performance",
  volatility: "Volatility",
  upside_volatility: "Upside Volatility",
  downside_volatility: "Downside Volatility",
  maximum_drawdown: "Maximum Drawdown",
  peak_date: "Peak Date",
  trough_date: "Trough Date",
  recovery_date: "Recovery Date",
  peak_to_trough_days: "Peak to Trough Days",
  time_to_recovery: "Time to Recovery",
  ulcer_index: "Ulcer Index",
  pain_index: "Pain Index",
  average_drawdown_duration: "Avg Drawdown Duration",
  sharpe_ratio: "Sharpe Ratio",
  sortino_ratio: "Sortino Ratio",
  calmar_ratio: "Calmar Ratio",
  return_to_drawdown_ratio: "Return/Drawdown",
  omega_ratio: "Omega Ratio",
  gain_loss_ratio: "Gain/Loss Ratio",
  profit_factor: "Profit Factor",
  num_positive_periods: "# Positive Periods",
  num_negative_periods: "# Negative Periods",
  percent_positive_periods: "% Positive Periods",
  percent_negative_periods: "% Negative Periods",
  best_period: "Best Period",
  worst_period: "Worst Period",
  average_positive_periods: "Avg Positive Period",
  average_negative_periods: "Avg Negative Period",
  longest_winning_streak: "Longest Win Streak",
  longest_losing_streak: "Longest Lose Streak",
  skewness: "Skewness",
  kurtosis: "Kurtosis",
  historical_var: "Historical VaR",
  historical_cvar: "Historical CVaR",
  relative_cumulative_performance: "Relative Cumulative",
  relative_annualized_performance: "Relative Annualized",
  relative_volatility: "Relative Volatility",
  beta: "Beta",
  correlation: "Correlation",
  tracking_error: "Tracking Error",
  information_ratio: "Information Ratio",
};

// Complete statistics groups for UI organization (all 38 stats)
export const STATISTIC_GROUPS = {
  performance: {
    label: "Performance",
    fields: [
      "cumulative_performance",
      "annualized_performance",
    ] as StatisticField[],
  },
  risk: {
    label: "Risk",
    fields: [
      "volatility",
      "upside_volatility",
      "downside_volatility",
    ] as StatisticField[],
  },
  drawdowns: {
    label: "Drawdowns",
    fields: [
      "maximum_drawdown",
      "peak_date",
      "trough_date",
      "recovery_date",
      "peak_to_trough_days",
      "time_to_recovery",
      "ulcer_index",
      "pain_index",
      "average_drawdown_duration",
    ] as StatisticField[],
  },
  ratios: {
    label: "Ratios",
    fields: [
      "sharpe_ratio",
      "sortino_ratio",
      "calmar_ratio",
      "return_to_drawdown_ratio",
      "omega_ratio",
      "gain_loss_ratio",
      "profit_factor",
    ] as StatisticField[],
  },
  distribution: {
    label: "Distribution",
    fields: [
      "num_positive_periods",
      "num_negative_periods",
      "percent_positive_periods",
      "percent_negative_periods",
      "best_period",
      "worst_period",
      "average_positive_periods",
      "average_negative_periods",
      "longest_winning_streak",
      "longest_losing_streak",
      "skewness",
      "kurtosis",
      "historical_var",
      "historical_cvar",
    ] as StatisticField[],
  },
  relative: {
    label: "Relative (vs Benchmark)",
    fields: [
      "relative_cumulative_performance",
      "relative_annualized_performance",
      "relative_volatility",
      "beta",
      "correlation",
      "tracking_error",
      "information_ratio",
    ] as StatisticField[],
  },
};

// ============================================================================
// FREQUENCY-BASED STATISTIC ORGANIZATION
// ============================================================================

// Statistics that are frequency-agnostic (same result regardless of frequency)
// These only depend on start/end values, not intermediate data points
export const FREQUENCY_AGNOSTIC_STATS: StatisticField[] = [
  "cumulative_performance",
  "annualized_performance",
  // Relative performance stats
  "relative_cumulative_performance",
  "relative_annualized_performance",
];

// Statistics that are frequency-sensitive (results vary by frequency)
// These depend on the granularity of intermediate data points
export const FREQUENCY_SENSITIVE_STATS: StatisticField[] = [
  // Risk stats
  "volatility",
  "upside_volatility",
  "downside_volatility",
  // Drawdown stats (depend on price series granularity)
  "maximum_drawdown",
  "peak_date",
  "trough_date",
  "recovery_date",
  "peak_to_trough_days",
  "time_to_recovery",
  "average_drawdown_duration",
  // Ratios
  "sharpe_ratio",
  "sortino_ratio",
  "calmar_ratio",
  "return_to_drawdown_ratio",
  "omega_ratio",
  "gain_loss_ratio",
  "profit_factor",
  // Distribution stats
  "num_positive_periods",
  "num_negative_periods",
  "percent_positive_periods",
  "percent_negative_periods",
  "best_period",
  "worst_period",
  "average_positive_periods",
  "average_negative_periods",
  "longest_winning_streak",
  "longest_losing_streak",
  "skewness",
  "kurtosis",
  "historical_var",
  "historical_cvar",
  "ulcer_index",
  "pain_index",
  // Relative risk stats
  "relative_volatility",
  "beta",
  "correlation",
  "tracking_error",
  "information_ratio",
];

// Frequency types
export type StatFrequency = "daily" | "monthly" | "quarterly";

// Trailing period types (for stats calculated over trailing N years)
export type TrailingPeriod = "1y" | "2y" | "3y" | "4y" | "5y" | "10y" | "si";

export const TRAILING_PERIODS: TrailingPeriod[] = ["1y", "2y", "3y", "4y", "5y", "10y", "si"];

export const TRAILING_PERIOD_LABELS: Record<TrailingPeriod, string> = {
  "1y": "1 Year",
  "2y": "2 Years",
  "3y": "3 Years",
  "4y": "4 Years",
  "5y": "5 Years",
  "10y": "10 Years",
  "si": "Full Period",
};

// Stats available for trailing period calculation
export const TRAILING_PERIOD_STATS: StatisticField[] = [
  "cumulative_performance",
  "annualized_performance",
  "volatility",
  "sharpe_ratio",
  "sortino_ratio",
  "maximum_drawdown",
];

// Extended statistic identifier that includes frequency or trailing period
// Format: "stat_field" for agnostic, "stat_field:frequency" for sensitive, "stat_field:trailing_Xy" for trailing periods
export type FrequencyStatisticId = string;

// Parse a frequency statistic ID
export function parseFrequencyStatId(id: FrequencyStatisticId): {
  field: StatisticField;
  frequency: StatFrequency | null;
  trailingPeriod: TrailingPeriod | null;
} {
  if (id.includes(":")) {
    const parts = id.split(":");
    const field = parts[0] as StatisticField;
    const modifier = parts[1];
    
    // Check if it's a trailing period (e.g., "trailing_3y" or "trailing_si")
    if (modifier.startsWith("trailing_")) {
      const period = modifier.replace("trailing_", "") as TrailingPeriod;
      // Check for optional frequency as third part (e.g., "volatility:trailing_si:monthly")
      const frequency = parts[2] ? (parts[2] as StatFrequency) : null;
      return { field, frequency, trailingPeriod: period };
    }
    
    // Otherwise it's a frequency
    return { field, frequency: modifier as StatFrequency, trailingPeriod: null };
  }
  return { field: id as StatisticField, frequency: null, trailingPeriod: null };
}

// Build a frequency statistic ID
export function buildFrequencyStatId(
  field: StatisticField,
  frequency: StatFrequency | null,
  trailingPeriod?: TrailingPeriod | null
): FrequencyStatisticId {
  if (trailingPeriod) {
    return `${field}:trailing_${trailingPeriod}`;
  }
  if (frequency && FREQUENCY_SENSITIVE_STATS.includes(field)) {
    return `${field}:${frequency}`;
  }
  return field;
}

// Get display label for a frequency statistic
export function getFrequencyStatLabel(id: FrequencyStatisticId): string {
  const { field, frequency, trailingPeriod } = parseFrequencyStatId(id);
  const baseLabel = STATISTIC_FIELD_LABELS[field] || field;
  
  if (trailingPeriod) {
    return `${baseLabel} (${TRAILING_PERIOD_LABELS[trailingPeriod]})`;
  }
  
  if (frequency) {
    const freqLabels: Record<StatFrequency, string> = {
      daily: "Daily",
      monthly: "Monthly",
      quarterly: "Quarterly",
    };
    return `${baseLabel} (${freqLabels[frequency]})`;
  }
  return baseLabel;
}

// Get the trailing period date range
export function getTrailingPeriodDateRange(
  trailingPeriod: TrailingPeriod,
  asOfDate: Date = new Date()
): { start_date: string; end_date: string } {
  const endDate = new Date(asOfDate);

  if (trailingPeriod === "si") {
    // Placeholder - actual date resolved by backend via time_range_preset
    return {
      start_date: endDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    };
  }

  const startDate = new Date(asOfDate);
  const years = parseInt(trailingPeriod.replace("y", ""));
  startDate.setFullYear(startDate.getFullYear() - years);

  return {
    start_date: startDate.toISOString().split("T")[0],
    end_date: endDate.toISOString().split("T")[0],
  };
}

// Tab-based organization for StatisticsSelector
export const STATISTICS_TABS = {
  performance: {
    id: "performance",
    label: "Performance",
    description: "Returns (frequency-agnostic)",
    groups: {
      returns: {
        label: "Returns",
        stats: ["cumulative_performance", "annualized_performance"] as StatisticField[],
      },
      relative: {
        label: "Relative Performance",
        stats: [
          "relative_cumulative_performance",
          "relative_annualized_performance",
        ] as StatisticField[],
      },
    },
    frequency: null as StatFrequency | null,
  },
  daily: {
    id: "daily",
    label: "Daily Stats",
    description: "Risk metrics calculated on daily returns",
    groups: {
      risk: {
        label: "Risk",
        stats: ["volatility", "upside_volatility", "downside_volatility"] as StatisticField[],
      },
      drawdowns: {
        label: "Drawdowns",
        stats: [
          "maximum_drawdown",
          "peak_date",
          "trough_date",
          "recovery_date",
          "peak_to_trough_days",
          "time_to_recovery",
          "average_drawdown_duration",
        ] as StatisticField[],
      },
      ratios: {
        label: "Ratios",
        stats: [
          "sharpe_ratio",
          "sortino_ratio",
          "calmar_ratio",
          "return_to_drawdown_ratio",
          "omega_ratio",
          "gain_loss_ratio",
          "profit_factor",
        ] as StatisticField[],
      },
      distribution: {
        label: "Distribution",
        stats: [
          "num_positive_periods",
          "num_negative_periods",
          "percent_positive_periods",
          "percent_negative_periods",
          "best_period",
          "worst_period",
          "average_positive_periods",
          "average_negative_periods",
          "longest_winning_streak",
          "longest_losing_streak",
          "skewness",
          "kurtosis",
          "historical_var",
          "historical_cvar",
          "ulcer_index",
          "pain_index",
        ] as StatisticField[],
      },
      relative: {
        label: "Relative Risk",
        stats: [
          "relative_volatility",
          "beta",
          "correlation",
          "tracking_error",
          "information_ratio",
        ] as StatisticField[],
      },
    },
    frequency: "daily" as StatFrequency,
  },
  monthly: {
    id: "monthly",
    label: "Monthly Stats",
    description: "Risk metrics calculated on monthly returns",
    groups: {
      risk: {
        label: "Risk",
        stats: ["volatility", "upside_volatility", "downside_volatility"] as StatisticField[],
      },
      drawdowns: {
        label: "Drawdowns",
        stats: [
          "maximum_drawdown",
          "peak_date",
          "trough_date",
          "recovery_date",
          "peak_to_trough_days",
          "time_to_recovery",
          "average_drawdown_duration",
        ] as StatisticField[],
      },
      ratios: {
        label: "Ratios",
        stats: [
          "sharpe_ratio",
          "sortino_ratio",
          "calmar_ratio",
          "return_to_drawdown_ratio",
          "omega_ratio",
          "gain_loss_ratio",
          "profit_factor",
        ] as StatisticField[],
      },
      distribution: {
        label: "Distribution",
        stats: [
          "num_positive_periods",
          "num_negative_periods",
          "percent_positive_periods",
          "percent_negative_periods",
          "best_period",
          "worst_period",
          "average_positive_periods",
          "average_negative_periods",
          "longest_winning_streak",
          "longest_losing_streak",
          "skewness",
          "kurtosis",
          "historical_var",
          "historical_cvar",
          "ulcer_index",
          "pain_index",
        ] as StatisticField[],
      },
      relative: {
        label: "Relative Risk",
        stats: [
          "relative_volatility",
          "beta",
          "correlation",
          "tracking_error",
          "information_ratio",
        ] as StatisticField[],
      },
    },
    frequency: "monthly" as StatFrequency,
  },
  quarterly: {
    id: "quarterly",
    label: "Quarterly Stats",
    description: "Risk metrics calculated on quarterly returns",
    groups: {
      risk: {
        label: "Risk",
        stats: ["volatility", "upside_volatility", "downside_volatility"] as StatisticField[],
      },
      drawdowns: {
        label: "Drawdowns",
        stats: [
          "maximum_drawdown",
          "peak_date",
          "trough_date",
          "recovery_date",
          "peak_to_trough_days",
          "time_to_recovery",
          "average_drawdown_duration",
        ] as StatisticField[],
      },
      ratios: {
        label: "Ratios",
        stats: [
          "sharpe_ratio",
          "sortino_ratio",
          "calmar_ratio",
          "return_to_drawdown_ratio",
          "omega_ratio",
        ] as StatisticField[],
      },
      distribution: {
        label: "Distribution",
        stats: [
          "num_positive_periods",
          "num_negative_periods",
          "percent_positive_periods",
          "percent_negative_periods",
          "best_period",
          "worst_period",
          "average_positive_periods",
          "average_negative_periods",
        ] as StatisticField[],
      },
      relative: {
        label: "Relative Risk",
        stats: [
          "relative_volatility",
          "beta",
          "correlation",
          "tracking_error",
          "information_ratio",
        ] as StatisticField[],
      },
    },
    frequency: "quarterly" as StatFrequency,
  },
  trailing: {
    id: "trailing",
    label: "Trailing Periods",
    description: "Performance and risk stats over 1Y, 2Y, 3Y, 4Y, 5Y, 10Y periods and since inception",
    groups: {
      "1y": {
        label: "1 Year",
        stats: TRAILING_PERIOD_STATS,
      },
      "2y": {
        label: "2 Years",
        stats: TRAILING_PERIOD_STATS,
      },
      "3y": {
        label: "3 Years",
        stats: TRAILING_PERIOD_STATS,
      },
      "4y": {
        label: "4 Years",
        stats: TRAILING_PERIOD_STATS,
      },
      "5y": {
        label: "5 Years",
        stats: TRAILING_PERIOD_STATS,
      },
      "10y": {
        label: "10 Years",
        stats: TRAILING_PERIOD_STATS,
      },
      "si": {
        label: "Since Inception",
        stats: TRAILING_PERIOD_STATS,
      },
    },
    frequency: null as StatFrequency | null,
    isTrailingPeriod: true,
  },
};

export type StatisticsTabId = keyof typeof STATISTICS_TABS;

// All available statistics as a flat array
export const ALL_STATISTIC_FIELDS: StatisticField[] = Object.values(STATISTIC_GROUPS)
  .flatMap((group) => group.fields);

// Default fields for initial display
export const DEFAULT_STATISTIC_FIELDS: StatisticField[] = [
  "cumulative_performance",
  "annualized_performance",
  "volatility",
  "maximum_drawdown",
  "sharpe_ratio",
];

// ============================================================================
// PERIOD-BASED RETURNS (calculated via date ranges, not backend stat fields)
// ============================================================================

export type PeriodType =
  // Standard to-date periods (relative to selected end date)
  | "mtd"
  | "qtd"
  | "ytd"
  // Calendar years (last 10)
  | "cy_2025" | "cy_2024" | "cy_2023" | "cy_2022" | "cy_2021"
  | "cy_2020" | "cy_2019" | "cy_2018" | "cy_2017" | "cy_2016"
  // Calendar quarters (last 20 = 5 years)
  | `q${"1" | "2" | "3" | "4"}_${number}`
  // Calendar months (last 60 = 5 years)
  | `m${string}_${number}`;

export interface PeriodDefinition {
  id: string;
  name: string;
  group: string;
  getDateRange: (asOfDate: Date) => { start: string; end: string } | null;
}

// Helper to format date as YYYY-MM-DD
function formatDate(date: Date): string {
  return date.toISOString().split("T")[0];
}

// Helper to get last day of month
function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

// Helper to get last day of quarter
function lastDayOfQuarter(year: number, quarter: number): Date {
  const quarterEndMonth = quarter * 3 - 1; // Q1=2 (Mar), Q2=5 (Jun), Q3=8 (Sep), Q4=11 (Dec)
  return lastDayOfMonth(year, quarterEndMonth);
}

// Generate period definitions dynamically based on current date
export function generatePeriodDefinitions(asOfDate: Date = new Date()): PeriodDefinition[] {
  const periods: PeriodDefinition[] = [];
  const currentYear = asOfDate.getFullYear();
  const currentMonth = asOfDate.getMonth();
  const currentQuarter = Math.floor(currentMonth / 3) + 1;

  // ========== Standard To-Date Periods ==========
  periods.push({
    id: "mtd",
    name: "MTD",
    group: "To-Date",
    getDateRange: (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      // Start = last day of previous month
      const start = lastDayOfMonth(year, month - 1);
      return { start: formatDate(start), end: formatDate(date) };
    },
  });

  periods.push({
    id: "qtd",
    name: "QTD",
    group: "To-Date",
    getDateRange: (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      // Start = last day of previous quarter
      const prevQuarterEnd = quarter === 1
        ? lastDayOfQuarter(year - 1, 4)
        : lastDayOfQuarter(year, quarter - 1);
      return { start: formatDate(prevQuarterEnd), end: formatDate(date) };
    },
  });

  periods.push({
    id: "ytd",
    name: "YTD",
    group: "To-Date",
    getDateRange: (date) => {
      const year = date.getFullYear();
      // Start = Dec 31 of previous year
      const start = new Date(year - 1, 11, 31);
      return { start: formatDate(start), end: formatDate(date) };
    },
  });

  // ========== Calendar Years (last 20) ==========
  for (let i = 0; i < 20; i++) {
    const year = currentYear - i;
    const isCurrentYear = i === 0;
    
    periods.push({
      id: `cy_${year}`,
      name: isCurrentYear ? `${year} (YTD)` : `${year}`,
      group: "Calendar Years",
      getDateRange: (date) => {
        const start = new Date(year - 1, 11, 31); // Dec 31 of prior year
        const end = isCurrentYear ? date : new Date(year, 11, 31); // Dec 31 or current date
        // Skip if as-of date is before this year
        if (date.getFullYear() < year) return null;
        return { start: formatDate(start), end: formatDate(end) };
      },
    });
  }

  // ========== Calendar Quarters (last 20 = 5 years) ==========
  let qYear = currentYear;
  let qQuarter = currentQuarter;
  
  for (let i = 0; i < 20; i++) {
    const year = qYear;
    const quarter = qQuarter;
    const isCurrentQuarter = i === 0;
    const quarterNames = ["Q1", "Q2", "Q3", "Q4"];
    
    periods.push({
      id: `q${quarter}_${year}`,
      name: isCurrentQuarter ? `${quarterNames[quarter - 1]} ${year} (QTD)` : `${quarterNames[quarter - 1]} ${year}`,
      group: "Calendar Quarters",
      getDateRange: (date) => {
        // Start = last day of previous quarter
        const prevQuarterEnd = quarter === 1
          ? lastDayOfQuarter(year - 1, 4)
          : lastDayOfQuarter(year, quarter - 1);
        // End = last day of this quarter (or current date if current quarter)
        const quarterEnd = isCurrentQuarter ? date : lastDayOfQuarter(year, quarter);
        // Skip if as-of date is before this quarter started
        const quarterStart = new Date(year, (quarter - 1) * 3, 1);
        if (date < quarterStart) return null;
        return { start: formatDate(prevQuarterEnd), end: formatDate(quarterEnd) };
      },
    });
    
    // Move to previous quarter
    qQuarter--;
    if (qQuarter === 0) {
      qQuarter = 4;
      qYear--;
    }
  }

  // ========== Calendar Months (last 60 = 5 years) ==========
  let mYear = currentYear;
  let mMonth = currentMonth;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  for (let i = 0; i < 60; i++) {
    const year = mYear;
    const month = mMonth;
    const isCurrentMonth = i === 0;
    
    periods.push({
      id: `m${month + 1}_${year}`,
      name: isCurrentMonth ? `${monthNames[month]} ${year} (MTD)` : `${monthNames[month]} ${year}`,
      group: "Calendar Months",
      getDateRange: (date) => {
        // Start = last day of previous month
        const prevMonthEnd = lastDayOfMonth(year, month - 1);
        // End = last day of this month (or current date if current month)
        const monthEnd = isCurrentMonth ? date : lastDayOfMonth(year, month);
        // Skip if as-of date is before this month started
        const monthStart = new Date(year, month, 1);
        if (date < monthStart) return null;
        return { start: formatDate(prevMonthEnd), end: formatDate(monthEnd) };
      },
    });
    
    // Move to previous month
    mMonth--;
    if (mMonth < 0) {
      mMonth = 11;
      mYear--;
    }
  }

  return periods;
}

// Get period definitions as a lookup map
export function getPeriodDefinitionsMap(asOfDate: Date = new Date()): Map<string, PeriodDefinition> {
  const periods = generatePeriodDefinitions(asOfDate);
  return new Map(periods.map((p) => [p.id, p]));
}

// Period groups for the selector UI
export const PERIOD_GROUPS = {
  toDate: {
    label: "To-Date",
    description: "Periods relative to selected end date",
  },
  calendarYears: {
    label: "Calendar Years",
    description: "Last 20 calendar years",
  },
  calendarQuarters: {
    label: "Calendar Quarters", 
    description: "Last 5 years of quarters (20 total)",
  },
  calendarMonths: {
    label: "Calendar Months",
    description: "Last 5 years of months (60 total)",
  },
};

// ============================================================================
// PERIOD RETURNS API TYPES (Backend-calculated)
// ============================================================================

export interface PeriodReturnRequest {
  resources: Array<{
    resource_type: "product" | "portfolio" | "benchmark" | "index";
    resource_id: string;
    stream_type?: string;
  }>;
  periods: string[];
  as_of_date?: string;
  frequency?: string;
}

export interface PeriodReturn {
  period_id: string;
  period_name: string;
  start_date: string;
  end_date: string;
  value: number | null;
  error: string | null;
}

export interface ResourcePeriodReturns {
  resource_type: string;
  resource_id: string;
  stream_type: string;
  period_returns: PeriodReturn[];
}

export interface PeriodReturnResponse {
  as_of_date: string;
  resources: ResourcePeriodReturns[];
  available_periods: Array<{
    id: string;
    name: string;
    group: string;
  }>;
}

// List of relative statistics that require a benchmark
export const RELATIVE_STATISTICS: StatisticField[] = [
  "relative_cumulative_performance",
  "relative_annualized_performance",
  "relative_volatility",
  "beta",
  "correlation",
  "tracking_error",
  "information_ratio",
];

// ============================================================================
// RESOURCE BENCHMARK LOOKUP TYPES
// ============================================================================

export interface ResourceBenchmarkInfo {
  resource_type: string;
  resource_id: string;
  has_benchmark: boolean;
  benchmark_id?: string;
  benchmark_type?: "benchmark" | "product" | "portfolio";
  benchmark_name?: string;
}

export interface ResourceBenchmarksRequest {
  resources: Array<{
    resource_type: "product" | "portfolio" | "benchmark" | "index";
    resource_id: string;
    stream_type?: string;
  }>;
}

export interface ResourceBenchmarksResponse {
  resources: ResourceBenchmarkInfo[];
}

// ============================================================================
// Stats Views - Saved Column Configurations
// ============================================================================

/**
 * A column definition for a statistic in a stats view.
 * Represents a single column showing a calculated statistic.
 */
export interface StatisticColumn {
  type: "statistic";
  /** The statistic field (e.g., "volatility", "sharpe_ratio") */
  field: StatisticField;
  /** Frequency for frequency-sensitive stats (e.g., "daily", "monthly") */
  frequency?: StatFrequency;
  /** Lookback period (e.g., "si", "3y", "5y") */
  lookback: TrailingPeriod;
}

/**
 * A column definition for a period return in a stats view.
 * Represents a single column showing a calendar period return.
 */
export interface PeriodReturnColumn {
  type: "period_return";
  /** The period ID (e.g., "cy_2024", "q1_2025", "mtd", "ytd") */
  period: string;
}

/**
 * A column in a stats view - either a statistic or a period return.
 */
export type StatsViewColumn = StatisticColumn | PeriodReturnColumn;

/**
 * A saved stats view configuration.
 * Views are stored per company and can be loaded by any user in that company.
 */
export interface StatsView {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  /** Ordered list of columns to display */
  columns: StatsViewColumn[];
  show_benchmarks: boolean;
  is_default: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface StatsViewListResponse {
  views: StatsView[];
  total: number;
}

export interface StatsViewCreate {
  name: string;
  description?: string;
  columns: StatsViewColumn[];
  show_benchmarks?: boolean;
  is_default?: boolean;
}

export interface StatsViewUpdate {
  name?: string;
  description?: string;
  columns?: StatsViewColumn[];
  show_benchmarks?: boolean;
  is_default?: boolean;
}

// ============================================================================
// UNIFIED TABLE API TYPES
// ============================================================================

/**
 * Request for the unified statistics table endpoint.
 * Sends all columns in order and receives all data back in the same order.
 */
export interface StatisticsTableRequest {
  resources: Array<{
    resource_type: "product" | "portfolio" | "benchmark" | "index";
    resource_id: string;
    stream_type?: string;
  }>;
  /** Columns in display order - backend returns values in this exact order */
  columns: StatsViewColumn[];
  /** Start date for calculations (may be overridden by time_range_preset) */
  start_date: string;
  /** End date for calculations */
  end_date: string;
  /** Default frequency for calculations */
  frequency?: "daily" | "weekly" | "monthly";
  /** Optional preset that overrides start_date (e.g., "MAX_COMMON", "MAX") */
  time_range_preset?: string;
  /** Annual risk-free rate for Sharpe/Sortino calculations (e.g., 0.02 for 2%). Defaults to 0. */
  risk_free_rate?: number;
  extend_incomplete_with_proxy?: boolean;
}

/**
 * Value for a single column in the response.
 */
export interface ColumnValue {
  value: number | null;
  error?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * A row of data for a single resource.
 */
export interface ResourceRow {
  resource_type: string;
  resource_id: string;
  stream_type: string;
  /** Values in the same order as request columns */
  values: ColumnValue[];
  proxy_extension?: ProxyExtensionMeta | null;
}

/**
 * Response from the unified statistics table endpoint.
 * Columns and values maintain the exact order from the request.
 */
export interface StatisticsTableResponse {
  /** Resolved start date (may differ from request for MAX presets) */
  start_date: string;
  /** End date */
  end_date: string;
  /** Columns in same order as request */
  columns: StatsViewColumn[];
  /** Resource rows with values in column order */
  resources: ResourceRow[];
}

// ============================================================================
// Conversion Helpers - Convert between columns array and legacy format
// ============================================================================
